
import { useTranslation } from "@/hooks/use-translation"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Heart, Calendar, Gamepad2 } from "lucide-react"
import { getAvailableGames, getLocalizedGameId, Game } from "@/lib/games-data"
import { useFavorites } from "@/context/favorites-context"
import { FavoriteStar } from "@/components/ui/favorite-star"
import { SEOMeta } from "@/components/seo/seo-meta"
import { InContentAd } from "@/components/adsense"

export function HomePage() {
  const { t, navigate: localizedNavigate, language } = useTranslation()
  const { favorites } = useFavorites()

  const availableGames = getAvailableGames(language)
  const favoriteGames = availableGames.filter(game => favorites.includes(game.id))
  const otherGames = availableGames.filter(game => !favorites.includes(game.id))

  const handleGameClick = (gameId: string) => {
    const localizedId = getLocalizedGameId(gameId, language)
    localizedNavigate(`/game/${localizedId}`)
  }

  const GameCard = ({ game }: { game: Game }) => {
    const Icon = game.icon

    return (
      <Card
        className="hover:shadow-md transition-shadow cursor-pointer group"
        onClick={() => handleGameClick(game.id)}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                <Icon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">{t(`games.${game.id}.name`)}</CardTitle>
                  <FavoriteStar toolId={game.id} />
                </div>
                <CardDescription className="text-sm">
                  {t(`games.${game.id}.description`)}
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-1">
            {game.tags?.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {t(`tags.${tag}`)}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <SEOMeta
        title={t('app.title')}
        description={t('app.subtitle')}
        keywords={`${t("navigation.dailyGames").toLowerCase()}, ${t("navigation.dailyGames")}, jeux quotidiens, word games, puzzle games, online games`}
      />

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="space-y-8">
          {/* Hero Section */}
          <div className="text-center space-y-3 py-4">
            <div className="mb-2">
              <h1 className="text-4xl font-bold">{t('app.title')}</h1>
              <p className="text-xl text-muted-foreground">{t('app.subtitle')}</p>
            </div>

            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{t("home.totalGames", { count: availableGames.length })}</span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="h-4 w-4" />
                <span>{t("home.dailyUpdates")}</span>
              </div>
            </div>
          </div>

          {/* Advertisement */}
          <InContentAd className="mx-auto" />

          {/* Favorite Games */}
          {favoriteGames.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-6">
                <Heart className="h-5 w-5 text-red-500" />
                <h2 className="text-2xl font-bold">{t("home.favoriteGames")}</h2>
                <Badge variant="secondary" className="ml-2">
                  {favoriteGames.length}
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {favoriteGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            </section>
          )}

          {/* All Games */}
          <section>
            <div className="flex items-center gap-2 mb-6">
              <Gamepad2 className="h-5 w-5" />
              <h2 className="text-2xl font-bold">
                {favoriteGames.length > 0 ? t("home.otherGames") : t("home.allGamesTitle")}
              </h2>
              <Badge variant="secondary" className="ml-2">
                {otherGames.length}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {otherGames.map((game) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>
          </section>

          {/* Bottom Advertisement */}
          <InContentAd className="mx-auto" />
        </div>
      </div>
    </>
  )
} 